'use client';

import React, { useState, useEffect } from 'react';

interface InquiryPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const MONTHS = [
'January', 'February', 'March', 'April', 'May', 'June',
'July', 'August', 'September', 'October', 'November', 'December'];


const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 4 }, (_, i) => currentYear + i);

const PROJECT_TYPES = [
'Music Video',
'Photography',
'Short Form',
'Brand/Event',
'Other'];


export default function InquiryPanel({ isOpen, onClose }: InquiryPanelProps) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [projectType, setProjectType] = useState('');
  const [projectMonth, setProjectMonth] = useState('');
  const [projectYear, setProjectYear] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {document.body.style.overflow = '';};
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/enquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone, email, projectType, projectMonth, projectYear, message }),
      });
      if (!res.ok) {
        const data = await res.json();
        setError(data.error || 'Something went wrong. Please try again.');
      } else {
        setSubmitted(true);
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setSubmitted(false);
      setName('');
      setPhone('');
      setEmail('');
      setProjectType('');
      setProjectMonth('');
      setProjectYear('');
      setMessage('');
      setError('');
    }, 600);
  };

  return (
    <div
      className={`inquiry-panel ${isOpen ? 'open' : ''}`}
      role="dialog"
      aria-modal="true"
      aria-label="Enquiry panel">

      <div className="h-full flex flex-col">
        {/* Header */}
        <div
          className="flex items-start justify-between px-8 py-7 border-b"
          style={{ borderColor: 'rgba(245,242,237,0.08)' }}>

          <div>
            <p
              className="text-[10px] uppercase tracking-[0.22em] mb-1.5"
              style={{ color: 'var(--brayer-red)', fontFamily: 'Manrope, sans-serif' }}>WANT TO FEEL SOMETHING?


            </p>
            <h2
              className="text-xl font-bold leading-tight"
              style={{ color: 'var(--cotton-rag)', fontFamily: 'Manrope, sans-serif' }}>

              Let's Work Together
            </h2>
            <p
              className="text-xs mt-1.5 leading-relaxed"
              style={{ color: 'var(--plate-gray)', fontFamily: 'Manrope, sans-serif' }}>Fill out the form below and i'll be in touch.


            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-9 h-9 flex-shrink-0 flex items-center justify-center border transition-colors duration-200 mt-1"
            style={{ borderColor: 'rgba(176,179,184,0.2)', color: 'var(--plate-gray)' }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--cotton-rag)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--cotton-rag)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(176,179,184,0.2)';
              (e.currentTarget as HTMLButtonElement).style.color = 'var(--plate-gray)';
            }}
            aria-label="Close enquiry panel">

            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
              <path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 px-8 py-8 overflow-y-auto no-scrollbar">
          {submitted ?
          <div className="flex flex-col justify-center h-full py-12">
              {/* Red accent line */}
              <div
              className="w-8 mb-10"
              style={{ height: '2px', background: 'var(--brayer-red)' }} />

              <h3
              className="text-3xl font-black leading-tight mb-5"
              style={{ color: 'var(--cotton-rag)', fontFamily: 'Manrope, sans-serif', letterSpacing: '-0.02em' }}>
                Got it{name ? `, ${name.split(' ')[0]}` : ''}.
              </h3>
              <p
              className="text-base leading-relaxed mb-10"
              style={{ color: 'var(--plate-gray)', fontFamily: 'Manrope, sans-serif' }}>
                You'll hear from me soon.
              </p>

              {/* Subtle divider */}
              <div style={{ borderTop: '1px solid rgba(245,242,237,0.07)', marginBottom: '2rem' }} />

              <button
              onClick={handleClose}
              className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold transition-colors duration-200 self-start"
              style={{ color: 'var(--plate-gray)', fontFamily: 'Manrope, sans-serif' }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--cotton-rag)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.color = 'var(--plate-gray)';
              }}>
                <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                  <path d="M13 7H1M1 7l5-5M1 7l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Back to site
              </button>
            </div> :

          <form onSubmit={handleSubmit} className="flex flex-col gap-6">

              {/* Name */}
              <div>
                <label
                className="block text-[10px] uppercase tracking-[0.22em] mb-2"
                style={{ color: 'var(--plate-gray)', fontFamily: 'Manrope, sans-serif' }}
                htmlFor="inq-name">

                  Name *
                </label>
                <input
                id="inq-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your full name"
                className="press-input" />

              </div>

              {/* Phone */}
              <div>
                <label
                className="block text-[10px] uppercase tracking-[0.22em] mb-2"
                style={{ color: 'var(--plate-gray)', fontFamily: 'Manrope, sans-serif' }}
                htmlFor="inq-phone">

                  Phone
                </label>
                <input
                id="inq-phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Your phone number"
                className="press-input" />

              </div>

              {/* Email */}
              <div>
                <label
                className="block text-[10px] uppercase tracking-[0.22em] mb-2"
                style={{ color: 'var(--plate-gray)', fontFamily: 'Manrope, sans-serif' }}
                htmlFor="inq-email">

                  Email *
                </label>
                <input
                id="inq-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="press-input" />

              </div>

              {/* Project Type */}
              <div>
                <label
                className="block text-[10px] uppercase tracking-[0.22em] mb-2"
                style={{ color: 'var(--plate-gray)', fontFamily: 'Manrope, sans-serif' }}
                htmlFor="inq-project-type">

                  Project Type
                </label>
                <select
                id="inq-project-type"
                value={projectType}
                onChange={(e) => setProjectType(e.target.value)}
                className="press-input"
                style={{ cursor: 'pointer' }}>

                  <option value="">Select a type…</option>
                  {PROJECT_TYPES.map((type) =>
                <option key={type} value={type}>{type}</option>
                )}
                </select>
              </div>

              {/* Project Date */}
              <div>
                <label
                className="block text-[10px] uppercase tracking-[0.22em] mb-2"
                style={{ color: 'var(--plate-gray)', fontFamily: 'Manrope, sans-serif' }}>

                  Project Date
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <select
                  value={projectMonth}
                  onChange={(e) => setProjectMonth(e.target.value)}
                  className="press-input"
                  style={{ cursor: 'pointer' }}
                  aria-label="Project month">

                    <option value="">Month</option>
                    {MONTHS.map((m) =>
                  <option key={m} value={m}>{m}</option>
                  )}
                  </select>
                  <select
                  value={projectYear}
                  onChange={(e) => setProjectYear(e.target.value)}
                  className="press-input"
                  style={{ cursor: 'pointer' }}
                  aria-label="Project year">

                    <option value="">Year</option>
                    {YEARS.map((y) =>
                  <option key={y} value={y}>{y}</option>
                  )}
                  </select>
                </div>
              </div>

              {/* Anything else */}
              <div>
                <label
                className="block text-[10px] uppercase tracking-[0.22em] mb-2"
                style={{ color: 'var(--plate-gray)', fontFamily: 'Manrope, sans-serif' }}
                htmlFor="inq-message">

                  Anything else to share?
                </label>
                <textarea
                id="inq-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tell me about your vision, timeline, or anything else that would help…"
                rows={4}
                className="press-input resize-none"
                style={{ paddingTop: '0.75rem' }} />

              </div>

              {/* Divider */}
              <div style={{ borderTop: '1px solid rgba(245,242,237,0.07)', marginTop: '-0.25rem' }} />

              {/* Submit */}
              <button
              type="submit"
              disabled={loading}
              className="w-full py-4 text-sm font-black uppercase tracking-[0.18em] transition-all duration-300 flex items-center justify-center gap-3 group disabled:opacity-60 disabled:cursor-not-allowed"
              style={{
                background: 'var(--brayer-red)',
                color: 'var(--cotton-rag)',
                fontFamily: 'Manrope, sans-serif'
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  (e.currentTarget as HTMLButtonElement).style.background = 'var(--brayer-red-dark)';
                  (e.currentTarget as HTMLButtonElement).style.letterSpacing = '0.22em';
                }
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--brayer-red)';
                (e.currentTarget as HTMLButtonElement).style.letterSpacing = '0.18em';
              }}>
                <span>{loading ? 'Sending…' : "Let's Go"}</span>
                {!loading && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                    style={{ transition: 'transform 0.2s ease' }}
                    className="group-hover:translate-x-1">
                    <path d="M4 10h12M12 5l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </button>

              {error && (
                <p className="text-center text-xs" style={{ color: 'var(--brayer-red)', fontFamily: 'Manrope, sans-serif' }}>
                  {error}
                </p>
              )}

              {/* Closing note */}
              <p className="text-center text-[10px] tracking-[0.15em] uppercase" style={{ color: 'rgba(245,242,237,0.3)', fontFamily: 'Manrope, sans-serif' }}>
                No bots. No automation. Just a real response within 48 hours.
              </p>

            </form>
          }
        </div>
      </div>
    </div>);

}